/**
 * Pulling the town out of a delivery address.
 *
 * Addresses arrive as free text from the customer app and from Google's
 * autocomplete, so they range from `"Hlavná 1, Šamorín"` to
 * `"Hlavná 1/A, 931 01 Šamorín, Trnavský kraj, Slovensko"`. The kitchen prints
 * only the town on its cards, because a cook reading twenty cards needs to know
 * which village a driver is going to, not the house number.
 *
 * Ported from legacy `pages/kitchen.tsx`. It lives here rather than in the
 * module because dispatch and the trip log want the same answer (§5).
 */

/** Countries. They are never the town, and they are always last when present. */
const COUNTRIES = [
  'slovensko',
  'slovakia',
  'česko',
  'czechia',
  'czech republic',
  'česká republika',
  'magyarország',
  'hungary',
  'polska',
  'poland',
  'österreich',
  'austria',
]

/** Administrative regions, which Google inserts between town and country. */
const REGION_PATTERNS = [
  /kraj$/i,
  /^západné\s/i,
  /^východné\s/i,
  /^stredné\s/i,
  /^bratislavský/i,
  /^trnavský/i,
  /^trenčiansky/i,
  /^nitriansky/i,
  /^žilinský/i,
  /^banskobystrický/i,
  /^prešovský/i,
  /^košický/i,
  /okres$/i,
  /^okres\s/i,
]

const POSTCODE_ONLY = /^\d{3}\s?\d{2}$/
const POSTCODE_PREFIX = /^\d{3}\s?\d{2}\s+/
const HOUSE_NUMBER_PREFIX = /^\S*\d+\S*\s+/

function isStreetOrNumber(part: string): boolean {
  return /^\d+[a-zA-Z/]*$/.test(part) || /\d+\/\d+/.test(part) || /^\d+\s/.test(part)
}

export function cityFromAddress(address: string): string {
  const parts = address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  if (parts.length <= 1) return address.trim()

  const meaningful = parts.filter((part) => {
    if (COUNTRIES.includes(part.toLowerCase())) return false
    if (REGION_PATTERNS.some((pattern) => pattern.test(part))) return false
    if (POSTCODE_ONLY.test(part)) return false
    return true
  })
  if (meaningful.length === 0) return address.trim()

  const candidates = meaningful.filter((part) => {
    // `931 01 Šamorín` is a town with its postcode attached, not a street.
    if (POSTCODE_PREFIX.test(part)) return true
    if (isStreetOrNumber(part)) return false
    const firstDigit = part.match(/\d/)
    if (/\d+\/\d+/.test(part) && (firstDigit?.index ?? Infinity) < 3) return false
    return true
  })
  // Everything looked like a street. The last meaningful part is the best guess.
  if (candidates.length === 0) return meaningful[meaningful.length - 1] ?? address.trim()

  const withPostcode = candidates.find((part) => POSTCODE_PREFIX.test(part))
  if (withPostcode) return withPostcode.replace(POSTCODE_PREFIX, '').trim()

  const last = candidates[candidates.length - 1] ?? ''
  if (HOUSE_NUMBER_PREFIX.test(last)) {
    return last.replace(HOUSE_NUMBER_PREFIX, '').trim() || last
  }
  return last
}
